// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import AjvKeywords from "ajv-keywords";

import actionCreator from "@modules/service/actionCreator";
import { getActiveMonitoringPromptAlerts } from "@modules/shared/selectors";
import { getAbsenceMonitoringSchema, getAbsenceMonitoringDraft } from "@modules/service/selectors";

import { removeNullAndUndefinedProps } from "@transformers/object";
import type { AlertShort } from "@modules/shared/reducer/alerts/types";
import type {
  AbsenceMonitoring,
  AbsenceMonitoringLevel,
} from "@modules/service/reducer/absence-monitoring/types";

import { Button, View, type StepProps } from "@components";
import { AbsenceMonitoringLevelsForm } from "@modules/service/components";

type OwnProps = $Exact<{
  editing: boolean,
  schema: ?Object,
}>;

type PropsFromState = $Exact<{
  alerts: Array<AlertShort> | null,
  schema: Object,
  draft: ?AbsenceMonitoring,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

function StepLevels(props: Props) {
  const data = props.draft?.content?.levels || [];
  const schema = {
    type: "array",
    uniqueItemProperties: ["name"],
    items: props.schema.properties.levels.items,
  };

  const [validationErrors, setValidationErrors] = useState(null);
  const [isAllValid, setIsAllValid] = useState(false);

  useEffect(() => {
    updateLevels(data);
  }, []);

  useEffect(() => {
    validateData();
  }, data);

  function updateLevels(levels: Array<AbsenceMonitoringLevel>) {
    props.setDraft({
      ...props.draft,
      content: {
        ...props.draft?.content,
        levels,
      },
    });
  }

  function submitLevels() {
    if (props.handleSubmit) {
      props.handleSubmit(data, true);
    }
  }

  function validateData() {
    if (!!!data.length) {
      setIsAllValid(false);
      return;
    }

    ////////////// CHECK SCHEMA VALIDATION
    let schemaErrors = [];
    const ajv = new AjvKeywords(new Ajv());
    const validate = ajv.compile(schema);
    const _data = data ? data.map(removeNullAndUndefinedProps) : null;
    let isValid = validate(_data);

    if (!isValid) {
      console.warn("Step Levels validation error: ", validate.errors);

      const regex = /\[(.*?)\]/;
      schemaErrors = validate.errors.map(err => {
        const str = err.dataPath;
        const match = str.match(regex);
        if (err.keyword === "required") {
          return {
            value: err.params.missingProperty,
            path: +match[1],
          };
        } else {
          return {
            value: str.slice(str.indexOf(".") + 1),
            path: match && +match[1],
          };
        }
      });
    }

    ////////////// CHECK validation for alerts and movements
    let additionalErrors = [];
    const totalLevels = data.length;
    // check missing alerts except last level
    data.map((level, index) => {
      if (index === 0 || (index !== 0 && index < totalLevels - 1)) {
        const alerts = level.alerts || [];
        const movements = level.movements || [];
        // check alerts
        if (alerts.length === 0) {
          additionalErrors.push({
            value: "alerts",
            path: index,
          });
        }
        // check movements
        if (movements.length === 0 || movements.length < 2) {
          additionalErrors.push({
            value: "movements",
            path: index,
          });
        }
      }
    });

    const errors = [...schemaErrors, ...additionalErrors];
    const allValid = errors.length === 0;

    if (!allValid) {
      setIsAllValid(false);
      setValidationErrors([...schemaErrors, ...additionalErrors]);
    } else {
      setIsAllValid(true);
      setValidationErrors(null);
    }
  }

  if (!props.editable) {
    return <div />;
  }

  return (
    <View gap="4">
      {!isAllValid && (
        <View className="message-box">
          An absence monitoring process must have a MINIMUM of 2 levels. (i.e. Level 0 and Level 1
          as a minimum)
        </View>
      )}
      <AbsenceMonitoringLevelsForm
        data={data}
        alerts={props.alerts}
        onChange={updateLevels}
        validationErrors={validationErrors}
      />
      {props.editing ? null : (
        <View alignItems="end">
          <Button onClick={submitLevels} disabled={!isAllValid}>
            {props.t("ABSENCE_MONITORING_STEP_LEVELS_NEXT_BUTTON")}
          </Button>
        </View>
      )}
    </View>
  );
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: getAbsenceMonitoringSchema(state),
    draft: getAbsenceMonitoringDraft(state),
    alerts: getActiveMonitoringPromptAlerts(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAbsenceMonitoringDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepLevels);
